import {
  GRID_GAP,
  useAllParticipants,
  useMainParticipant,
} from '@cometchat/calls-sdk-core';
import React, { useLayoutEffect, useRef, useState } from 'react';
import Tile from '../tile/Tile.web';
import GroupAvatarTile from './GroupAvatarTile.web';
import './SidebarLayout.web.css';

interface SidebarTileLayout {
  mainTileWidth: number;
  mainTileHeight: number;
  sidebarWidth: number;
  maxSidebarTiles: number;
}

const SidebarLayout: React.FC = () => {
  const participants = useAllParticipants();
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<SidebarTileLayout>({
    mainTileWidth: 0,
    mainTileHeight: 0,
    sidebarWidth: 200,
    maxSidebarTiles: 10,
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const mainParticipant = useMainParticipant();

  useLayoutEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      // Calculate layout based on participant count
      let sidebarWidth = 0;
      let mainTileWidth = width;
      const mainTileHeight = height;
      mainTileWidth = width - sidebarWidth - GRID_GAP;
      if (participants.length > 1 && participants.length <= 9) {
        // Single column layout for 2-9 participants
        // (6-9 participants show 4 individual + 1 GroupAvatarTile = 5 tiles total, still single column)
        sidebarWidth = 210;
      } else {
        // 2-column grid layout for 10+ participants
        sidebarWidth = 440; // Increased width for 2 columns
      }

      setLayout({
        mainTileWidth,
        mainTileHeight,
        sidebarWidth,
        maxSidebarTiles: 10,
      });
    });

    observer.observe(container);
    return () => observer.unobserve(container);
  }, [participants.length]);

  if (participants.length === 0) return null;

  const sidebarParticipants = participants;

  // Determine max visible tiles based on screen size
  const isTablet = windowWidth >= 681 && windowWidth <= 1024;
  const isMobile = windowWidth <= 680;

  const participantCount = sidebarParticipants.length;

  // Overflow logic to maximize space usage (no empty spaces):
  // Desktop:
  //   - 5 or less: Show all as individual tiles
  //   - 6-9: Show 4 individual tiles + 1 GroupAvatarTile for remaining
  //   - 10: Show all 10 as individual tiles (5 per column, fills 2-column layout)
  //   - 11+: Show 9 individual + 1 GroupAvatarTile (10 tiles = fills 2-column layout)
  // Tablet:
  //   - 5 or less: Show all as individual tiles
  //   - 6-9: Show 4 individual tiles + 1 GroupAvatarTile (5 tiles = 100% height)
  //   - 10+: Show 4 individual + 1 GroupAvatarTile (5 tiles = 100% height)
  // Mobile:
  //   - 8 or less: Show all as individual tiles
  //   - 9+: Show 7 individual + 1 GroupAvatarTile (8 tiles = fills 2x4 grid)

  // Determine if we should show overflow tile
  let hasOverflow;
  if (isMobile) {
    // Mobile: Show overflow only for 9+ participants
    hasOverflow = participantCount >= 9;
  } else if (isTablet) {
    // Tablet: Show overflow for 6+ participants
    hasOverflow = participantCount >= 6;
  } else {
    // Desktop
    if (participantCount <= 5) {
      hasOverflow = false;
    } else if (participantCount >= 6 && participantCount <= 9) {
      hasOverflow = true;
    } else if (participantCount === 10) {
      hasOverflow = false; // Desktop shows all 10
    } else {
      hasOverflow = true; // 11+ use overflow
    }
  }

  // Adjust maxVisibleSidebar based on participant count, screen size and overflow condition
  let maxVisibleSidebar;
  if (hasOverflow) {
    if (isMobile) {
      // Mobile: 9+ participants show 7 individual + 1 GroupAvatarTile
      maxVisibleSidebar = 7;
    } else if (isTablet) {
      // Tablet: 6+ participants show 4 individual + 1 GroupAvatarTile
      maxVisibleSidebar = 4;
    } else {
      // Desktop
      if (participantCount >= 6 && participantCount <= 9) {
        maxVisibleSidebar = 4; // 4 tiles + 1 GroupAvatarTile
      } else {
        maxVisibleSidebar = 9; // 11+: 9 tiles + 1 GroupAvatarTile
      }
    }
  } else {
    // No overflow: Show all participants as individual tiles
    maxVisibleSidebar = sidebarParticipants.length;
  }

  const visibleSidebarParticipants = sidebarParticipants.slice(
    0,
    maxVisibleSidebar,
  );

  // Generate CSS classes based on participant count for mobile layouts
  const getContainerClasses = () => {
    const baseClass = 'cometchat-calls-sidebar-layout-container';
    if (!isMobile) return baseClass;

    const totalParticipants = participants.length;
    return `${baseClass} cometchat-calls-mobile-${totalParticipants}-participants`;
  };

  const renderSidebarLayout = () => {
    // Hide sidebar completely if only 1 participant
    if (sidebarParticipants.length === 1) return null;

    return (
      <div
        className="cometchat-calls-sidebar-layout-sidebar-container"
        style={
          windowWidth > 1024
            ? { width: layout.sidebarWidth }
            : { width: '100%' }
        }>
        <div className="cometchat-calls-sidebar-layout-grid">
          {/* Show visible participants as individual tiles */}
          {visibleSidebarParticipants.map(participant => (
            <div
              key={participant.ruid}
              className="cometchat-calls-sidebar-layout-tile">
              <Tile participant={participant} />
            </div>
          ))}

          {/* Show overflow tile for 6-9 participants or 11+ participants */}
          {hasOverflow && (
            <div
              className={
                isTablet
                  ? 'cometchat-calls-sidebar-layout-tile cometchat-calls-grouptile-component'
                  : 'cometchat-calls-sidebar-layout-tile'
              }>
              <GroupAvatarTile
                startIndex={maxVisibleSidebar}
                style={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className={getContainerClasses()}>
      {/* Main participant - full screen viewport */}
      <div
        className="cometchat-calls-sidebar-layout-main-view"
        style={
          windowWidth > 1024
            ? {
                width: layout.mainTileWidth,
                height: layout.mainTileHeight,
              }
            : {}
        }>
        <Tile participant={mainParticipant} />
      </div>

      {/* Sidebar with other participants */}
      {renderSidebarLayout()}
    </div>
  );
};

export default SidebarLayout;
